/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from "react";
import { css } from "@emotion/react";
import CampaignCard from "../ResultComponents/CampaignCard";
import SummaryCard from "../ResultComponents/SummaryCard";
import ComplianceReferenceCard, {
  AnalysisSummaryResult,
} from "../ResultComponents/ComplianceReferenceCard";
import InlineInformation from "../ResultComponents/InlineInformation";
import Loader from "../ResultComponents/Loader";
import InformationText from "../InformationText";
import { useUserDataService } from "services/useUserDataService";
import { AnalysisResultProvider } from "./AnalysisResultContext";
import { REACT_APP_NEW_THEME } from "services/config";
import PositiveRepReferenceCard from "../ResultComponents/PositiveRepReferenceCard";
import { useLanguage } from "LanguageContext";
import PageLoader from "./../../../MainComponents/Loader/Loader";

const pageStyles = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  margin: 0px;
`;
interface ResultProps {
  analysisData: any;
  loading: boolean;
}
export default function Result(props: Readonly<ResultProps>) {
  const { t } = useLanguage();
  const userDataRes = useUserDataService();
  const [displayInline, setDisplayInline] = useState<boolean>(true);
  const [validAnalysis, setValidAnalysis] = useState<AnalysisSummaryResult[]>(
    []
  );
  const [invalidAnalysis, setInvalidAnalysis] = useState<
    AnalysisSummaryResult[]
  >([]);
  const [middleAnalysis, setMiddleAnalysis] = useState<AnalysisSummaryResult[]>(
    []
  );
  const [notApplicableAnalysis, setNotApplicableAnalysis] = useState<AnalysisSummaryResult[]>(
    []
  );
  useEffect(() => {
    const valid: AnalysisSummaryResult[] = [];
    const middle: AnalysisSummaryResult[] = [];
    const invalid: AnalysisSummaryResult[] = [];
    const notApplicable: AnalysisSummaryResult[] = [];

    if (props.analysisData) {
      props.analysisData.responses.forEach((item: any) => {
        const evaluation = item?.response?.evaluation;
        let title = item?.response?.title;
        if (item?.front_title?.startsWith("frameworks_") && item?.front_title?.includes("title_principle_")) {
          title = item?.front_title;
        }
        
        const isValid = item?.response?.is_valid;
        const feedbacks = item?.feedbacks;
        const id = item?.id;
        const content = item?.response?.content; // Ensure content is defined
        const index = item?.response?.index; // Ensure content is defined

        const analysisResult: AnalysisSummaryResult = { index, title, evaluation, feedbacks, id, content };

        if (isValid === -1) {
          notApplicable.push(analysisResult);
        } else if (isValid === 2) {
          middle.push(analysisResult);
        } else if (isValid) {
          valid.push(analysisResult);
        } else if (!isValid) {
          invalid.push(analysisResult);
        }
      });

      setValidAnalysis(valid);
      setMiddleAnalysis(middle);
      setInvalidAnalysis(invalid);
      setNotApplicableAnalysis(notApplicable);
    }
  }, [props.analysisData]);
  if (!props?.analysisData || props?.loading) {
    return (
      <div>
        <PageLoader />
      </div>
    )
  } 
  return (
    <div css={pageStyles}>
      <CampaignCard
        title={props.analysisData?.name}
        referenceFrame={props.analysisData?.ruleset?.map((val: { stage: any; front_name: any }) => `${t(val.front_name)} ${(val.stage && !window.location.hostname.includes(REACT_APP_NEW_THEME)) ? `(${t(val.stage)})` : ""}`).join(', ')}
        advertiser={props.analysisData?.project?.advertiser}
        language={props.analysisData?.locale_code}
        dateCreated={new Date(props.analysisData?.created_at).toDateString()}
        note={props.analysisData?.notes}
        project={props.analysisData?.project?.name}
        imageUrl={props.analysisData?.asset?.thumbnail_urls[0] ?props.analysisData?.asset?.thumbnail_urls[0] : props.analysisData?.asset?.original_url }
        original_url={props.analysisData?.asset?.original_url}
        imgType={props.analysisData?.asset?.mime_type}
        descriptionUrl="#"
        description={props?.analysisData?.config?.asset_description}
        showContextHint={props.analysisData?.status !== "finished"}
      />
      {props?.analysisData?.status !== "finished" ? (
        <Loader />
      ) : (
        <>
          <AnalysisResultProvider analysisData={props?.analysisData}>
              {displayInline && !userDataRes?.loading && (
                <InlineInformation setDisplayInline={setDisplayInline} showAnalysisResultConsent={userDataRes?.userData?.showAnalysisResultConsent}/>
              )}
              <SummaryCard analysisData={props.analysisData} isComplianceRefFrame={props.analysisData?.ruleset?.[0]?.category === 'compliance'}/>
              {(props.analysisData?.ruleset?.[0]?.category !== 'compliance') ? (
                <PositiveRepReferenceCard
                  resultData={props.analysisData}
                />)
                :<ComplianceReferenceCard
                  invalidAnalysis={invalidAnalysis}
                  middleAnalysis={middleAnalysis}
                  validAnalysis={validAnalysis}
                  notApplicableAnalysis={notApplicableAnalysis}
                  resultData={props.analysisData}
                />
              }
            <InformationText />
          </AnalysisResultProvider>
        </>
      )}
    </div>
  );
}
